import React from 'react';
import ListViewBookingCount from '../../ListViewBookingCount';
import ListViewCardChildren from '../ListViewCardChildren/ListViewCardChildren';
import ListViewCardDetails from '../ListViewCardDetails/ListViewCardDetails';
import { Meta } from '../../../../styled components/Typography';
import { HappeningNowDot } from '../../ListViewBookings/ListViewBookings.style';
import {
  BookingCard,
  CardHeader,
  CardTitle,
  CardTitleLabel
} from './ListViewCard.style';
import {
  bookingCellValue,
  bookingTimeRange,
  formatCellValue,
  RESOURCE_NAME_COLUMN_KEY,
  temporalStatus
} from '../../../../libs/listViewPresentation';
import { isHappeningNow, rowDisplayStatus } from '../../../listViewRowStatus';
import type {
  ListViewBooking,
  ListViewColumn,
  ListViewDiff,
  ListViewSetCollapse,
  TimeFormatSetting
} from '../../../types';

interface ListViewCardProps {
  booking: ListViewBooking;
  /* The column shown as the card's title; the others are its detail rows */
  titleColumn: ListViewColumn;
  detailColumns: ListViewColumn[];
  /* How the set of this card, and the sets among its members, are opened and
     closed */
  setCollapse: ListViewSetCollapse;
  statusByID: ListViewDiff['statusByID'];
  now: string;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* One booking as a card: its title column, its other column values, and — for a
   set whose members are asked for — its member bookings */
const ListViewCard = ({
  booking,
  titleColumn,
  detailColumns,
  setCollapse,
  statusByID,
  now,
  dateFormatSetting,
  timeFormatSetting
}: ListViewCardProps) => {
  const title = formatCellValue(
    bookingCellValue(booking, titleColumn),
    titleColumn,
    dateFormatSetting,
    timeFormatSetting
  );
  const marker = statusByID[booking.id];
  const temporal = temporalStatus(booking, now);
  const status = rowDisplayStatus(marker, temporal);
  const happeningNow = isHappeningNow(marker, temporal);
  return (
    <BookingCard
      $padding={16}
      $status={status}
      data-test-list-view-card
      data-test-row-status={status || undefined}
      data-test-row-happening-now={happeningNow || undefined}
    >
      <CardHeader>
        <CardTitleLabel>
          <CardTitle as="h3" $color="black" $deleted={status === 'deleted'}>
            {title}
          </CardTitle>
          {/* The count belongs to the resource's name, so it is shown here
              only where that is what the title says; a card titled with
              another column has it on its name's detail row instead */}
          {titleColumn.key === RESOURCE_NAME_COLUMN_KEY && (
            <ListViewBookingCount booking={booking} />
          )}
        </CardTitleLabel>
        {/* In front of the times rather than of the title: what the dot says
            is about the booking's period, and it is the period it marks */}
        {happeningNow && <HappeningNowDot label={i18n('Happening now')} />}
        <Meta tw="shrink-0 ml-8" $color="gray dark">
          {bookingTimeRange(booking, timeFormatSetting)}
        </Meta>
      </CardHeader>
      <ListViewCardDetails
        booking={booking}
        detailColumns={detailColumns}
        changedColumnKeys={marker ? marker.changedColumnKeys : []}
        dateFormatSetting={dateFormatSetting}
        timeFormatSetting={timeFormatSetting}
      />
      {booking.children.length > 0 && (
        <ListViewCardChildren
          setBooking={booking}
          setCollapse={setCollapse}
          statusByID={statusByID}
          timeFormatSetting={timeFormatSetting}
        />
      )}
    </BookingCard>
  );
};

export default ListViewCard;
